import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { env } from '@/lib/env'
import { fetchPublicInvite, readInviteToken, saveInviteToken, type PublicInvite } from '@/lib/invite'

const signupResponseSchema = z.object({
  status: z.string(),
  // M8.2: Beim Invite-Signup wird kein eigener Workspace angelegt — die
  // Antwort enthaelt dann kein workspace-Feld.
  workspace: z.object({ id: z.string(), name: z.string(), slug: z.string() }).optional(),
})

export function SignupPage() {
  const [params] = useSearchParams()
  const inviteParam = params.get('invite')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [workspaceName, setWorkspaceName] = useState('')
  const [language, setLanguage] = useState<'de' | 'en'>('de')
  const [sent, setSent] = useState(false)
  const [loading, setLoading] = useState(false)
  // M8.2: Einladungs-Kontext — bei gueltiger Einladung wird die E-Mail
  // vorbefuellt und der Token beim Submit mitgeschickt.
  const [invite, setInvite] = useState<PublicInvite | null>(null)
  const [inviteToken, setInviteToken] = useState<string | null>(null)
  const [inviteError, setInviteError] = useState(false)

  useEffect(() => {
    const token = inviteParam ?? readInviteToken()
    if (!token) return
    saveInviteToken(token)
    let cancelled = false
    fetchPublicInvite(token)
      .then((data) => {
        if (cancelled) return
        setInvite(data)
        setInviteToken(token)
        setEmail(data.email)
      })
      .catch(() => {
        // Ungueltige/abgelaufene Einladung: Hinweis zeigen, Formular bleibt
        // ohne Einladungs-Kontext nutzbar (kein inviteToken im Submit).
        if (!cancelled) setInviteError(true)
      })
    return () => {
      cancelled = true
    }
  }, [inviteParam])

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setLoading(true)
    try {
      const response = await fetch(`${env.VITE_API_URL.replace(/\/+$/, '')}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          workspaceName,
          language,
          ...(inviteToken ? { inviteToken } : {}),
        }),
      })
      const json = await response.json()
      if (!response.ok) throw new Error(json?.error?.message ?? 'Signup fehlgeschlagen')
      signupResponseSchema.parse(json)
      setSent(true)
      toast.success('Magic-Link verschickt.')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Signup fehlgeschlagen')
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-[400px]">
        <div className="mb-8 text-center">
          <h1 className="text-[26px] font-[650] leading-tight tracking-tight text-foreground">MindKeep</h1>
          <p className="mt-1 text-sm text-muted-foreground">KI-Wissensbasis für dein Unternehmen</p>
        </div>
        <Card>
        <CardHeader>
          <CardTitle>Kostenlos starten</CardTitle>
          <CardDescription>Lege deinen MindKeep-Workspace an.</CardDescription>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="space-y-4 text-sm">
              <p>Der Magic-Link ist unterwegs. Öffne ihn in diesem Browser, dann geht es direkt ins Onboarding.</p>
              <Button asChild variant="outline">
                <Link to="/login">Zum Login</Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                MindKeep ausprobieren — funktionierender Prototyp mit Beispiel-Inhalten. Kostenlos, ohne Bezahlweg.
              </p>
              {invite ? (
                <p className="rounded-md border bg-muted/40 p-3 text-sm" data-testid="signup-invite-info">
                  Du wurdest zu „<span className="break-words">{invite.workspace_name}</span>“ eingeladen — nach der Registrierung nimmst du die Einladung automatisch an.
                </p>
              ) : null}
              {inviteError ? (
                <p className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                  Einladung nicht gefunden oder abgelaufen.
                </p>
              ) : null}
              <form className="space-y-4" onSubmit={submit}>
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" value={name} onChange={(event) => setName(event.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail</Label>
                  <Input id="email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="workspace">Workspace-Name</Label>
                  <Input id="workspace" value={workspaceName} onChange={(event) => setWorkspaceName(event.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="language">Sprache</Label>
                  <select
                    id="language"
                    className="flex min-h-11 min-[761px]:min-h-9 w-full rounded-md border border-input bg-background px-3 text-[16px] min-[761px]:text-sm text-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    value={language}
                    onChange={(event) => setLanguage(event.target.value as 'de' | 'en')}
                  >
                    <option value="de">Deutsch</option>
                    <option value="en">English</option>
                  </select>
                </div>
                <Button className="w-full" disabled={loading}>
                  {loading ? 'Erstelle Workspace...' : 'Magic-Link senden'}
                </Button>
                <p className="text-xs text-muted-foreground">
                  Mit der Registrierung akzeptierst du die{' '}
                  <Link className="underline underline-offset-4 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm" to="/legal/privacy">Datenschutzerklärung</Link>
                  {' '}und{' '}
                  <Link className="underline underline-offset-4 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm" to="/legal/terms">Nutzungsbedingungen</Link>.
                </p>
              </form>
            </div>
          )}
        </CardContent>
        </Card>
      </div>
    </main>
  )
}
